import React, {useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {UniqueIdentifier} from '@dnd-kit/core';
import {Flex, Scrollbar, Text, Title} from '@priority-software/priority-style-react';
import {rectSortingStrategy} from '@dnd-kit/sortable';
import {HomepageGroups} from './modules/HomepageGroups/HomepageGroups';
import {homepageStore} from './store/HomepageStore';
import {isRTL} from '../../Localization';
import {getServerTexts} from './texts';
import {createDataTestId} from '../../utils/dataTestId';
import {getHomepageData, initPortletData} from './actions/data';
import {useScrollWrapDimensions} from '../../hooks/customScroll/useScrollWrapDimensions';
import {HomepageButtons} from './components/HomepageButtons/HomepageButtons';
import {getServerTexts as getTDLTexts} from '../../features/ToDoList/texts';
import {getGreetingBasedOnTime} from './utils';

export const Homepage = observer(() => {
    const [isLoading, setIsLoading] = useState(false);
    const {maxHeight, setWrapRef} = useScrollWrapDimensions();

    useEffect(() => {
        const fetchCompanyHomepage = async (showLoading?: boolean) => {
            if (showLoading) {
                setIsLoading(true);
            }

            Promise.all([
                getHomepageData(true),
                initPortletData(true),
                getServerTexts(),
                getTDLTexts()
            ]).finally(() => {
                setIsLoading(false);
            });
        };

        fetchCompanyHomepage(true);

        const companyChangeHandler = (event: any) => fetchCompanyHomepage();

        window.addEventListener('companyChange', companyChangeHandler);
        return () => {
            window.removeEventListener('companyChange', companyChangeHandler);
        };
    }, []);

    const {groups, items, updateHash} = homepageStore;

    const groupsMap = useMemo(() => {
        return [...groups.keys()].reduce((map, key) => {
            map.set(key, groups.get(key)?.items.map(item => item.exec) || []);
            return map;
        }, new Map());
    }, [groups]);

    const onDragEnd = async (itemsMap?: Map<UniqueIdentifier, UniqueIdentifier[]>) => {
        if (itemsMap) {
            const data = [...itemsMap.entries()]
                .map(([key, values]) => {
                    const group = groups.get(key as string);
                    return [group, ...values.map(value => items[value])];
                })
                .flat();

            await homepageStore.saveHomepageData(data);
        }
    };

    return (
        <Flex
            justifyContent="flex-start"
            direction="column"
            componentClasses="reactComp"
            {...createDataTestId('hp-common-wrapper', true)}
            style={{gap: 16}}
        >
            <Flex>
                <Title skin="h2" style={{whiteSpace: 'nowrap'}}>
                    {getGreetingBasedOnTime()}
                </Title>
                <HomepageButtons />
            </Flex>

            <div style={{flexGrow: 1, width: '100%'}} ref={setWrapRef}>
                {isLoading ? (
                    <Text skin="label-1" {...createDataTestId('hp-initial-loader', true)}>
                        Loading...
                    </Text>
                ) : (
                    <Scrollbar
                        rtl={isRTL()}
                        autoHeight
                        autoHeightMax={maxHeight}
                        shiftVerticalScrollPositionX={16}
                        {...createDataTestId('hp-groups')}
                    >
                        <HomepageGroups
                            itemsMap={groupsMap}
                            items={items}
                            groupsData={groups}
                            strategy={rectSortingStrategy}
                            onDragEnd={onDragEnd}
                            portletsKey={updateHash}
                        />
                    </Scrollbar>
                )}
            </div>
        </Flex>
    );
});
